import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import ScrollToTop from '@/components/Buttons/ScrollToTop';
import Footer from '@/components/Footer/Footer';
import FooterBanner from '@/components/FooterBanner/FooterBanner';
import Navigation from '@/components/Navigation/Navigation';
import Seo from '@/components/Seo/Seo';
import { LANGUAGES } from '@/lib/constants';
import { fetchAPI } from '@/lib/fetchAPI';
import { translate } from '@/lib/translate';
import Advantages from '@/modules/homepage/Advantages';
import Slider from '@/modules/homepage/Slider';
import Statistics from '@/modules/homepage/Statistics';
import SuccessStories from '@/modules/homepage/SuccessStories';
import VideoSection from '@/modules/homepage/VideoSection';

const Home: FC<any> = ({ data, lang, stories }) => {
	const [successStories, setSuccessStories] = useState<any[] | null>(null);

	useEffect(() => {
		if (!stories?.length || stories.length === 0) return;

		const success: any[] = [];

		stories.forEach((item) => {
			if (item.url === 'coca-cola') {
				success[0] = item;
			}
			if (item.url === 'wwf') {
				success[1] = item;
			}
			if (item.url === 'foodpanda') {
				success[2] = item;
			}
			if (item.url === 'ukrsibbank') {
				success[3] = item;
			}
			if (item.url === 'privatbank') {
				success[4] = item;
			}
			if (item.url === 'okko-pay-by-fishka') {
				success[5] = item;
			}
		});

		setSuccessStories(success);
	}, [stories]);

	return (
		<>
			<Seo
				url="/"
				title={translate(lang, {
					en: 'Viber Services to Promote Your Business',
					ru: 'Сервисы Viber для продвижения вашего бизнеса',
					ua: 'Сервіси Viber для просування вашого бізнесу',
				})}
				description={translate(lang, {
					en: 'Learn more about Viber for Business products and offerings: advertising, stickers, chatbots and Business Messages. Viber for Business is a powerful solution for brands, businesses and companies of all sizes.',
					ru: 'Узнайте больше о продуктах и предложениях Viber для бизнеса: рекламе, стикерах, чат-ботах и бизнес-сообщениях. Viber для бизнеса – это эффективное решение для брендов, бизнеса и компаний любых размеров.',
					ua: 'Дізнайтеся більше про продукти та пропозиції Viber для бізнесу: рекламу, стікери, чат-боти та бізнес-повідомлення. Viber для бізнесу – це ефективне рішення для брендів, бізнесу та компаній будь-якого розміру.',
				})}
			/>

			<Navigation lang={lang} />

			<Header>
				<Slider data={data?.hero?.slide} />
			</Header>

			<Statistics data={data?.statistic} />

			<VideoSection data={data?.videoSection} />

			<Advantages data={data?.advantage} />

			{successStories ? <SuccessStories data={successStories} /> : null}

			<ScrollToTop />

			<FooterBanner
				cta={data?.bottomBanner?.cta}
				title={data?.bottomBanner?.title}
				image={{
					alt: data?.bottomBanner?.image?.name,
					link: data?.bottomBanner?.image?.url,
				}}
			/>

			<Footer homepage lang={lang} />
		</>
	);
};

const Header = styled.div`
	width: 100%;
	max-width: 100vw;
	height: 100vh;
	max-height: 800px;
	position: relative;
	overflow: hidden;

	@media (max-width: 600px) {
		max-height: 600px;
	}

	@media (max-width: 530px) {
		height: 550px;
	}
`;

export async function getStaticProps({ params }) {
	const { lang } = params;
	const [page, stories] = await Promise.all([
		fetchAPI('/home', {
			lang,
			populate: [
				'hero',
				'hero.slide',
				'hero.slide.cta',
				'hero.slide.image',
				'statistic',
				'advantage',
				'advantage.items',
				'advantage.items.icon',
				'advantage.image',
				'videoSection',
				'videoSection.video',
				'bottomBanner',
				'bottomBanner.cta',
				'bottomBanner.image',
			],
		}),
		fetchAPI('/stories', { lang }),
	]);

	return {
		props: {
			lang,
			data: page?.data,
			stories: stories?.data,
		},
	};
}

export async function getStaticPaths() {
	const paths = LANGUAGES.map(({ code }) => ({ params: { lang: code } }));

	return { paths, fallback: false };
}

export default Home;
